import {
  PayoutHoldReason,
  SellerAccountStatus,
  SellerActionEmphasis,
  SellerActionKind,
  StripeAccountLinkType,
} from "@/Apollo/schema";
import {
  ACCOUNT_SETTINGS_ROUTE,
  CONTACT_US_ROUTE,
} from "@/common/static/routes.static";
import {
  PAYMENT_REASON_PARAM,
  PaymentCopyKey,
  STRIPE_REFRESH_PARAM,
  STRIPE_RETURN_PARAM,
  UPDATED_JUST_NOW_MINUTES,
} from "@/components/Account/payment/payment.static";

export type StripeLinkType = "ACCOUNT_ONBOARDING" | "ACCOUNT_UPDATE";

export type ActionColor = "brand" | "attention";

/**
 * The one solid button of the page, as the gateway decided it
 * (`StripeAccountDetails.actions`, see fromGatewayActions). `bank-form`
 * means the inline IBAN form's save button is that primary and the status
 * card renders none, so no button ever scrolls to another part of the page.
 */
export type PrimaryAction =
  | {
      kind: "stripe-link";
      linkType: StripeLinkType;
      /** Gateway label; null when its copy missed, then `labelKey` renders. */
      label: string | null;
      labelKey: PaymentCopyKey;
      color: ActionColor;
      disabled: boolean;
      /** The gateway's sentence on why the button is disabled; null when enabled or missed. */
      disabledReason: string | null;
    }
  | { kind: "bank-form" }
  | {
      kind: "route";
      href: string;
      label: string | null;
      labelKey: PaymentCopyKey;
      color: ActionColor;
    }
  | { kind: "none" };

export type SecondaryAction =
  | {
      kind: "stripe-link";
      linkType: StripeLinkType;
      label: string | null;
      labelKey: PaymentCopyKey;
      appearance: "outlined" | "minimal";
    }
  | {
      kind: "route";
      href: string;
      label: string | null;
      labelKey: PaymentCopyKey;
      appearance: "outlined" | "minimal";
    }
  | null;

export const ADDRESS_SETTINGS_HREF = `${ACCOUNT_SETTINGS_ROUTE}/address`;

/** One `StripeAccountDetails.actions` entry, as the query returns it. */
export type GatewayAction = {
  kind: SellerActionKind;
  emphasis: SellerActionEmphasis;
  link_type?: StripeAccountLinkType | null;
  label: string;
  disabled_reason?: string | null;
};

// The gateway's t() returns the raw key on a Lokalise miss; a key must only
// cost the text it names, never the button.
const RAW_ACTION_KEY_PREFIXES = ["account-action-", "verification-action-"];

const localizedLabel = (value: string | null | undefined): string | null =>
  !value ||
  !value.trim() ||
  RAW_ACTION_KEY_PREFIXES.some(prefix => value.startsWith(prefix))
    ? null
    : value;

const toLinkType = (
  value: StripeAccountLinkType | null | undefined
): StripeLinkType =>
  value === StripeAccountLinkType.AccountUpdate
    ? "ACCOUNT_UPDATE"
    : "ACCOUNT_ONBOARDING";

/** Pink while payouts are off or the account could not be created; green otherwise. */
export const actionColor = (status: SellerAccountStatus): ActionColor =>
  status === SellerAccountStatus.BlockedActionNeeded ||
  status === SellerAccountStatus.AccountNotCreated
    ? "attention"
    : "brand";

const routeFor = (
  kind: SellerActionKind
): { href: string; labelKey: PaymentCopyKey } | null => {
  switch (kind) {
    case SellerActionKind.AddressSettings:
      return { href: ADDRESS_SETTINGS_HREF, labelKey: "buttonCheckAddress" };
    case SellerActionKind.ContactSupport:
      return { href: CONTACT_US_ROUTE, labelKey: "buttonContactUs" };
    default:
      return null;
  }
};

const toPrimary = (
  action: GatewayAction | null,
  status: SellerAccountStatus
): PrimaryAction => {
  if (!action) {
    return { kind: "none" };
  }

  if (action.kind === SellerActionKind.StripeLink) {
    return {
      kind: "stripe-link",
      linkType: toLinkType(action.link_type),
      label: localizedLabel(action.label),
      labelKey: "buttonContinueAtStripe",
      color: actionColor(status),
      disabled: Boolean(action.disabled_reason),
      disabledReason: localizedLabel(action.disabled_reason),
    };
  }

  if (action.kind === SellerActionKind.BankAccountForm) {
    return { kind: "bank-form" };
  }

  const route = routeFor(action.kind);

  return route
    ? {
        kind: "route",
        ...route,
        label: localizedLabel(action.label),
        color: actionColor(status),
      }
    : { kind: "none" };
};

const toSecondary = (
  action: GatewayAction | null,
  status: SellerAccountStatus
): SecondaryAction => {
  if (!action) {
    return null;
  }

  // READY keeps its outlined view/edit button; everywhere else the secondary
  // is a text link so it never competes with the primary.
  const appearance =
    status === SellerAccountStatus.Ready ? "outlined" : "minimal";

  if (action.kind === SellerActionKind.StripeLink) {
    return {
      kind: "stripe-link",
      linkType: toLinkType(action.link_type),
      label: localizedLabel(action.label),
      labelKey:
        status === SellerAccountStatus.Ready
          ? "buttonViewOrEdit"
          : "buttonViewDetails",
      appearance,
    };
  }

  const route = routeFor(action.kind);

  return route
    ? {
        kind: "route",
        ...route,
        label: localizedLabel(action.label),
        appearance: "outlined",
      }
    : null;
};

/**
 * The gateway decides which buttons the page gets (one rule for web, iOS and
 * Android); this only maps each one to the surface that renders it and
 * guards against a label that missed Lokalise.
 */
export const fromGatewayActions = (
  actions: readonly GatewayAction[],
  status: SellerAccountStatus
): { primary: PrimaryAction; secondary: SecondaryAction } => ({
  primary: toPrimary(
    actions.find(action => action.emphasis === SellerActionEmphasis.Primary) ??
      null,
    status
  ),
  secondary: toSecondary(
    actions.find(
      action => action.emphasis === SellerActionEmphasis.Secondary
    ) ?? null,
    status
  ),
});

/** The status card's amount line; null where money is not the subject. */
export const amountLineKey = (
  status: SellerAccountStatus
): PaymentCopyKey | null => {
  switch (status) {
    case SellerAccountStatus.BlockedActionNeeded:
      return "amountWaitingForYou";
    case SellerAccountStatus.UnderReview:
      return "amountWaitingForCheck";
    case SellerAccountStatus.BlockedNotFixable:
      return "amountWaiting";
    default:
      return null;
  }
};

export const showsTaskChecklist = (status: SellerAccountStatus): boolean =>
  status === SellerAccountStatus.NotSetUp ||
  status === SellerAccountStatus.ActiveActionNeeded ||
  status === SellerAccountStatus.BlockedActionNeeded;

export const showsMoneyBlock = (status: SellerAccountStatus): boolean =>
  status === SellerAccountStatus.BlockedActionNeeded ||
  status === SellerAccountStatus.UnderReview ||
  status === SellerAccountStatus.BlockedNotFixable;

export const showsRecentPayouts = (status: SellerAccountStatus): boolean =>
  status === SellerAccountStatus.Ready ||
  status === SellerAccountStatus.ActiveActionNeeded;

/** What releases the money, per status, for the breakdown's order and balance lines. */
export const releaseCopyKeys = (
  status: SellerAccountStatus
): {
  group: PaymentCopyKey;
  order: PaymentCopyKey;
  balance: PaymentCopyKey;
} => {
  if (status === SellerAccountStatus.UnderReview) {
    return {
      group: "groupCheck",
      order: "orderReleaseCheck",
      balance: "balanceReleaseCheck",
    };
  }

  if (status === SellerAccountStatus.BlockedNotFixable) {
    return {
      group: "groupAccount",
      order: "orderReleaseResolved",
      balance: "balanceReleaseResolved",
    };
  }

  return {
    group: "groupAccount",
    order: "orderReleaseAccount",
    balance: "balanceReleaseAccount",
  };
};

export type HeldOrderLine = {
  id: string;
  title: string;
  payout: number;
  created: string | null;
  holdReason: PayoutHoldReason;
  threadId: string | null;
};

/** The tag an order under "Waiting for something else" carries. */
export const OTHER_HOLD_TAG: Partial<Record<PayoutHoldReason, PaymentCopyKey>> =
  {
    [PayoutHoldReason.Dispute]: "tagDispute",
    [PayoutHoldReason.Return]: "tagReturn",
    [PayoutHoldReason.Damage]: "tagDamage",
    [PayoutHoldReason.Refund]: "tagRefund",
  };

export const splitHeldOrders = (
  orders: HeldOrderLine[]
): { account: HeldOrderLine[]; other: HeldOrderLine[] } => ({
  account: orders.filter(
    order => order.holdReason === PayoutHoldReason.Account
  ),
  other: orders.filter(order => OTHER_HOLD_TAG[order.holdReason] !== undefined),
});

export const sumPayouts = (orders: { payout: number }[]): number =>
  Math.round(orders.reduce((total, order) => total + order.payout, 0) * 100) /
  100;

const DOCUMENT_REASON_LINES: [RegExp, PaymentCopyKey][] = [
  [/not_readable|corrupt/, "reasonNotReadable"],
  [/expired/, "reasonExpired"],
  [/too_large/, "reasonTooLarge"],
  [/type_not_supported|country_not_supported/, "reasonTypeNotSupported"],
  [/missing_front|missing_back/, "reasonMissingSide"],
  [/failed_copy|greyscale|photo_mismatch/, "reasonCopy"],
];

/**
 * Plain-words reason lines for a rejected document. Stripe's codes never
 * reach the screen: a code without a line here adds nothing, and fraud or
 * manipulation codes deliberately have no line (the gateway puts those
 * sellers in BLOCKED_NOT_FIXABLE).
 */
export const documentReasonLineKeys = (
  reasonCodes: readonly string[]
): PaymentCopyKey[] => {
  const keys: PaymentCopyKey[] = [];

  for (const code of reasonCodes) {
    for (const [pattern, key] of DOCUMENT_REASON_LINES) {
      if (pattern.test(code) && !keys.includes(key)) {
        keys.push(key);
      }
    }
  }

  return keys;
};

/** Whole business days (Mon to Fri) strictly after `from`, up to `now`. */
export const businessDaysSince = (from: Date, now: Date): number => {
  const cursor = new Date(from);
  cursor.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setHours(0, 0, 0, 0);
  let count = 0;

  while (cursor < end) {
    cursor.setDate(cursor.getDate() + 1);
    const day = cursor.getDay();

    if (day !== 0 && day !== 6) {
      count += 1;
    }
  }

  return count;
};

export const calendarDaysUntil = (deadline: Date, now: Date): number =>
  Math.ceil((deadline.getTime() - now.getTime()) / 86_400_000);

export type UpdatedLabel =
  | { copyKey: "updatedJustNow" }
  | { copyKey: "updatedMinutesAgo"; minutes: number }
  | { copyKey: "updatedOn"; date: string };

/** "Updated just now" inside the refresh window, minutes under an hour, the date after. */
export const describeUpdated = (
  asOf: string | null | undefined,
  now: Date
): UpdatedLabel | null => {
  if (!asOf) {
    return null;
  }

  const minutes = Math.floor(
    (now.getTime() - new Date(asOf).getTime()) / 60_000
  );

  if (minutes < UPDATED_JUST_NOW_MINUTES) {
    return { copyKey: "updatedJustNow" };
  }

  if (minutes < 60) {
    return { copyKey: "updatedMinutesAgo", minutes };
  }

  return { copyKey: "updatedOn", date: asOf };
};

export type HighlightTarget = "tasks" | "bank" | null;

/** Which block an email's `?reason=` highlights (the PayoutBlockerFamily values). */
export const highlightTargetFor = (reason: unknown): HighlightTarget => {
  switch (reason) {
    case "payout-setup-incomplete":
    case "account-verification":
      return "tasks";
    case "bank-account":
      return "bank";
    default:
      return null;
  }
};

const firstQueryValue = (value: string | string[] | undefined) =>
  Array.isArray(value) ? value[0] : value;

export type PaymentPageQuery = {
  stripeReturn: boolean;
  stripeRefresh: boolean;
  highlight: HighlightTarget;
};

export const readPaymentPageQuery = (
  query: Record<string, string | string[] | undefined>
): PaymentPageQuery => ({
  stripeReturn: firstQueryValue(query[STRIPE_RETURN_PARAM]) === "1",
  stripeRefresh: firstQueryValue(query[STRIPE_REFRESH_PARAM]) === "1",
  highlight: highlightTargetFor(firstQueryValue(query[PAYMENT_REASON_PARAM])),
});

/** The link type an expired Account Link is re-minted with. */
export const linkTypeForRefresh = (
  detailsSubmitted: boolean
): StripeLinkType =>
  detailsSubmitted ? "ACCOUNT_UPDATE" : "ACCOUNT_ONBOARDING";
